import * as React from "react";
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  TextField,
  Box,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import StarIcon from "@mui/icons-material/Star";

interface TemplateOption {
  template_id: string;
  template_name: string;
  is_primary: boolean;
}

interface Props {
  title: string;
  options: TemplateOption[];
  onSelect: (value: TemplateOption) => void;
  onButtonClick?: () => void; 
  disabled?: boolean;
}

export default function SplitTemplateButton({
  title,
  options,
  onSelect,
  onButtonClick,
  disabled,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleSelect = (option: TemplateOption) => {
    onSelect(option);
    setOpen(false);
  };

  const filteredOptions = options.filter((item) =>
    item?.template_name?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        disabled={disabled}
        sx={{
          height: "30px",
          marginRight: "10px",
          "& .MuiButton-root": {
            height: "30px",
            fontSize: "13px",
            textTransform: "capitalize",
            padding: "0 10px",
            minWidth: "auto",
          },
        }}
      >
        <Button onClick={onButtonClick}>{title}</Button>

        <Button
          onClick={e => {
            e.stopPropagation();
            handleToggle();
          }}
          sx={{
            minWidth: "28px",
            width: "28px",
            padding: 0,
          }}
        >
          <ArrowDropDownIcon sx={{ fontSize: "18px" }} />
        </Button>
      </ButtonGroup>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        placement="bottom-start"
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper sx={{ width: 320 }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box p={1}>
                  {/* Search */}
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search templates"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                      mb: 1,
                      "& .MuiInputBase-root": {
                        height: "28px",
                        fontSize: "12px",
                      },
                      "& input": {
                        padding: "4px 8px",
                      },
                    }}
                  />

                  {/* Menu */}
                  <MenuList
                    sx={{
                      padding: 0,
                    }}
                  >
                    {filteredOptions.map((option) => (
                      <MenuItem
                        key={option.template_id}
                        onClick={() => handleSelect(option)}
                        sx={{
                          minHeight: "28px",
                          fontSize: "13px",
                          padding: "4px 8px",
                        }}
                      >
                        {option.is_primary && (
                          <StarIcon
                            sx={{
                              fontSize: 14,
                              color: "#f5b301",
                              mr: 0.5,
                            }}
                          />
                        )}

                        {option.template_name}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
